<template>
  <div
    :class="autoHeight ? $style.growWrap : ''"
    :data-replicated-value="value"
    data-qa="textarea"
  >
    <textarea
      v-bind="$attrs"
      ref="input"
      :aria-invalid="invalid ? 'true' : 'false'"
      :class="[
        { 'form-control_invalid': invalid },
        { [$style.formControl]: true }
      ]"
      :style="{ minHeight }"
      :value="value"
      @input="handleInput"
      @paste="handlePaste"
      @keydown="handleKeydown"
    />
  </div>
</template>

<script setup lang="ts">
import { debounce } from 'lodash';
import { nextTick, onMounted, ref, useAttrs, useTemplateRef } from 'vue';

type Props = {
  value: string;
  invalid: boolean;
  focus: boolean;
  autoHeight: boolean;
  minHeight: number;
  submitOnEnter: boolean;
};

defineOptions({
  inheritAttrs: false
});

const {
  value,
  autoHeight = true,
  invalid,
  focus,
  submitOnEnter
} = defineProps<Partial<Props>>();

const emit = defineEmits<{
  inputValue: [string];
  input: [string];
  submit: [];
  paste: [string];
}>();

const _handlePaste = (event: any) => {
  emit('paste', event.target.value);
};

const handleInput = (event: any) => {
  emit('inputValue', event.target.value);
  emit('input', event);
};

const handlePasteDebounced = ref<(event: any) => void>();

const handlePaste = (event: any) => {
  setTimeout(() => {
    if (handlePasteDebounced.value) {
      handlePasteDebounced.value(event);
    }
  }, 100);
};

const attrs = useAttrs();

const handleKeydown = (event: any) => {
  if (!submitOnEnter) {
    return;
  }
  const { key, ctrlKey, metaKey } = event;
  const shouldSubmit = key === 'Enter' && (ctrlKey || metaKey);
  if (shouldSubmit) {
    event.preventDefault();
    event.stopPropagation();
    emit('submit');
    return;
  }
  return attrs.onKeydown?.(event);
};

const input = useTemplateRef<HTMLTextAreaElement>('input');

onMounted(() => {
  nextTick(() => {
    if (focus && input.value) {
      input.value.focus();
    }
  });
  handlePasteDebounced.value = debounce(_handlePaste, 500);
});
</script>

<style module>
.growWrap {
  display: grid;
}
.growWrap::after {
  content: attr(data-replicated-value) " ";
  white-space: pre-wrap;
  visibility: hidden;
  word-break: break-word;
}
.growWrap > textarea {
  resize: none;
  overflow: hidden;
  word-break: break-word;
}
.growWrap > textarea,
.growWrap::after {
  padding: var(--Space-400, 16px);
  border: 1px solid rgba(0, 0, 0, 0.16);
  font: inherit;

  grid-area: 1 / 1 / 2 / 2;
}

.formControl {
  border-radius: var(--Radius-x2, 8px);
  border: 1px solid rgba(0, 0, 0, 0.16);
  padding: var(--Space-400, 16px);
  margin: 0;
  width: 100%;
  max-width: 100%;
  min-height: 100px;
  resize: vertical;
  vertical-align: top;
  outline: 0 none;
  display: inline-block;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  background-color: transparent;
  background-image: none;

  &:focus {
    border-color: #2cc8df;
  }

  &[disabled] {
    opacity: 0.3;
    cursor: default;
    /* <3 Safari */
    -webkit-text-fill-color: var(--content-primary, #050505);
  }
}

@media not all and (min-resolution: 0.001dpcm) {
  .formControl[resize] {
    padding-left: 0;
    padding-right: 0;
    border-left: solid 16px;
    border-right: solid 16px;
    border-color: transparent;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' fill='none'%3E%3Cpath fill='%23666' fill-rule='evenodd' d='m1 8 7-7-.707-.707-7 7L1 8Zm4 0 3-3-.707-.707-3 3L5 8Z' clip-rule='evenodd'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: calc(100% + 14px) 100%;

    &:focus {
      box-shadow: 0 0 0 1px #2cc8df;
      border-color: transparent;
    }

    &::-webkit-resizer {
      display: none;
    }
  }
}
</style>

<i18n lang="json">{}</i18n>
